(() => {
  const API_NAME = 'LMPercentAnimation';
  const SELECTOR = '[data-lm-percent-animate]';

  /*
   * Si le fichier est accidentellement chargé plusieurs fois,
   * on réinitialise simplement les nouveaux éléments.
   */
  if (window[API_NAME]) {
    window[API_NAME].init(document);
    return;
  }

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /**
   * Retourne tous les blocs Pourcentage présents dans une zone.
   *
   * @param {Document|Element} root
   * @returns {HTMLElement[]}
   */
  const getPercentElements = (root = document) => {
    const elements = [];

    if (root instanceof Element && root.matches(SELECTOR)) {
      elements.push(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      elements.push(...root.querySelectorAll(SELECTOR));
    }

    return [...new Set(elements)];
  };

  /**
   * Affiche directement la valeur finale sans animation.
   *
   * @param {HTMLElement} element
   */
  const showFinalState = (element) => {
    element.dataset.lmPercentInitialized = 'true';
    element.classList.remove('lm-percent--will-animate');
    element.classList.add('is-visible');
  };

  let observer = null;

  if (
    !prefersReducedMotion &&
    'IntersectionObserver' in window
  ) {
    observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add('is-visible');
          currentObserver.unobserve(entry.target);
        });
      },
      {
        /*
         * Le cercle se déclenche lorsqu’environ 25 %
         * du bloc est visible.
         */
        threshold: 0.25,

        /*
         * Évite de démarrer trop tôt avant que le visiteur
         * arrive réellement sur la Méga section.
         */
        rootMargin: '0px 0px -8% 0px'
      }
    );
  }

  /**
   * Prépare un cercle pour son animation.
   *
   * @param {HTMLElement} element
   * @param {{ replay?: boolean, immediate?: boolean }} options
   */
  const preparePercent = (
    element,
    {
      replay = false,
      immediate = false
    } = {}
  ) => {
    if (
      !replay &&
      element.dataset.lmPercentInitialized === 'true'
    ) {
      return;
    }

    if (observer) {
      observer.unobserve(element);
    }

    element.dataset.lmPercentInitialized = 'true';

    if (prefersReducedMotion || !observer) {
      showFinalState(element);
      return;
    }

    /*
     * Retour à 0 % avant de déclencher la progression.
     */
    element.classList.remove('is-visible');
    element.classList.add('lm-percent--will-animate');

    /*
     * Force le navigateur à enregistrer l’état initial.
     * Nécessaire pour rejouer l’animation dans l’éditeur Shopify.
     */
    void element.offsetWidth;

    if (immediate) {
      /*
       * Lorsqu’un bloc vient d’être ajouté ou sélectionné
       * dans Shopify, l’animation démarre immédiatement.
       */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          element.classList.add('is-visible');
        });
      });

      return;
    }

    /*
     * Sur la boutique, le cercle attend d’arriver
     * dans la zone visible de l’écran.
     */
    observer.observe(element);
  };

  /**
   * Initialise tous les blocs présents dans une zone.
   *
   * @param {Document|Element} root
   * @param {{ replay?: boolean, immediate?: boolean }} options
   */
  const initializePercentBlocks = (
    root = document,
    options = {}
  ) => {
    getPercentElements(root).forEach((element) => {
      preparePercent(element, options);
    });
  };

  /**
   * Rejoue volontairement l’animation.
   *
   * @param {Document|Element} root
   * @param {boolean} immediate
   */
  const replayPercentBlocks = (
    root = document,
    immediate = true
  ) => {
    initializePercentBlocks(root, {
      replay: true,
      immediate
    });
  };

  window[API_NAME] = {
    init: initializePercentBlocks,
    replay: replayPercentBlocks
  };

  /*
   * Chargement normal de la boutique.
   */
  const start = () => {
    initializePercentBlocks(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  /*
   * Shopify vient de remplacer ou recharger une section.
   *
   * Les nouveaux cercles sont préparés puis attendent
   * d’être visibles avant de s’animer.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializePercentBlocks(event.target, {
        replay: true,
        immediate: false
      });
    }
  );

  /*
   * Un bloc vient d’être sélectionné dans l’éditeur.
   *
   * Cela se produit notamment juste après l’ajout
   * d’un nouveau bloc Pourcentage.
   */
  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const selectedBlock = event.target;

      if (!(selectedBlock instanceof Element)) {
        return;
      }

      const percentElement = selectedBlock.matches(SELECTOR)
        ? selectedBlock
        : selectedBlock.querySelector(SELECTOR);

      if (percentElement) {
        preparePercent(percentElement, {
          replay: true,
          immediate: true
        });

        return;
      }

      /*
       * Sécurité si Shopify sélectionne un parent
       * plutôt que le bloc Pourcentage lui-même.
       */
      initializePercentBlocks(selectedBlock, {
        replay: true,
        immediate: true
      });
    }
  );

  /*
   * Sécurité supplémentaire uniquement dans l’éditeur Shopify.
   *
   * Elle détecte un bloc ajouté dans le DOM même dans le cas
   * où l’événement de section n’est pas reçu immédiatement.
   */
  if (
    window.Shopify &&
    window.Shopify.designMode &&
    'MutationObserver' in window
  ) {
    const editorObserver = new MutationObserver(
      (mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) {
              return;
            }

            initializePercentBlocks(node, {
              replay: false,
              immediate: false
            });
          });
        });
      }
    );

    editorObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();